// Client mirrors `server/admin-handlers/adsAssistantRoles.ts` — keep token lists in sync.

import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AuthContextValue } from "@/contexts/AuthContext";

export const ADS_ASSISTANT_ACCESS_ROLES = [
  "sales",
  "trainer",
  "marketing",
  "admin",
  "owner",
  "dev",
  "ads_guidance_editor",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

export const ADS_GUIDANCE_EDITOR_ROLES = [
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "marketing",
  "trainer",
  "ads_guidance_editor",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "platform_trainer",
] as const;

/** Roles that may see template slot labeled fields (non–sales-only ingestors). Mirrors elevated Ads roles + dev; keep aligned with `adsAssistantRoles.ts`. */
export const ADS_SLOT_LABELED_FIELD_ROLES = [...ADS_GUIDANCE_EDITOR_ROLES, "dev"] as const;

/** Categories, templates, policies, and post quality (good/bad) — org admin/owner and marketing/trainer paths. */
export function canEditAdsGuidance(auth: { status: string; hasRole: (...r: string[]) => boolean }): boolean {
  return auth.status === "ready" && auth.hasRole(...ADS_GUIDANCE_EDITOR_ROLES);
}

/** Sales-only users (sales without any elevated Ads role) do not see slot-fill UI. */
export function canSeeAdsSlotLabeledFields(auth: { status: string; hasRole: (...r: string[]) => boolean }): boolean {
  return auth.status === "ready" && auth.hasRole(...ADS_SLOT_LABELED_FIELD_ROLES);
}

/** Graph / external post id on manual ingest — same visibility as slot-labeled fields (hide for sales-only). */
export function canSeeAdsGraphExternalId(auth: { status: string; hasRole: (...r: string[]) => boolean }): boolean {
  return canSeeAdsSlotLabeledFields(auth);
}

export function canAccessAdsAssistant(auth: AuthContextValue): boolean {
  return canAccessPrimaryMode("adsAssistant", auth);
}

/** Sales reps without trainer/marketing/guidance roles — personal coaching in header + strip. */
export function isAdsSalesFocusUser(auth: { status: string; hasRole: (...r: string[]) => boolean }): boolean {
  return auth.status === "ready" && auth.hasRole("sales") && !canEditAdsGuidance(auth);
}
